// src/components/dentally/DentallyPatientLinkChip.jsx
//
// Compact hero pill for the practice/lead-scoped Dentally patient link.
// Same pillBadge styling as the other hero metadata chips. States: Checking,
// linked, not linked, needs attention (ambiguous/malformed), could not check.
// This is patient identity only; booking sync state is a separate fact and is
// not implied by this chip.
import PropTypes from 'prop-types';
import { Box, Tooltip, Zoom } from '@mui/material';
import Iconify from '../Iconify';
import { pillBadge } from '../../styles/LeadDetailView';
import { getPatientLinkPresentation, TONE_COLOURS } from '../../utils/dentallyPatientLinkStatus';

const TOOLTIPS = {
  checking: 'Checking whether this lead is linked to a Dentally patient',
  linked: 'This lead is linked to a Dentally patient. Open Personal details to review.',
  unlinked: 'No Dentally patient is linked to this lead',
  attention: 'The Dentally patient link needs review. Open Personal details.',
  error: 'Dentally could not be checked just now. Open Personal details to retry.',
};

export default function DentallyPatientLinkChip({ outcome, patientId, onClick }) {
  const presentation = getPatientLinkPresentation(outcome);
  const colours = TONE_COLOURS[presentation.tone];
  const label = presentation.key === 'linked' && patientId != null
    ? `${presentation.label} · #${patientId}`
    : presentation.label;

  return (
    <Tooltip title={TOOLTIPS[presentation.key]} placement="bottom" TransitionComponent={Zoom} TransitionProps={{ timeout: 300 }}>
      <Box
        role="button"
        tabIndex={0}
        aria-label={`${presentation.label}. Open Personal details`}
        data-testid="dentally-patient-link-chip"
        data-state={presentation.key}
        onClick={onClick}
        onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onClick?.(event); } }}
        sx={{ ...pillBadge(colours.bg, colours.fg), cursor: 'pointer' }}
      >
        <Iconify icon={presentation.icon} width={16} height={16} />
        {label}
      </Box>
    </Tooltip>
  );
}

DentallyPatientLinkChip.propTypes = {
  outcome: PropTypes.string,
  patientId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onClick: PropTypes.func,
};

DentallyPatientLinkChip.defaultProps = {
  outcome: 'loading',
  patientId: null,
  onClick: undefined,
};
